import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock, MapPin, Phone, Wrench } from "@phosphor-icons/react";
import { AppShell } from "@/components/AppShell";
import { RatingForm } from "@/components/RatingForm";
import { RatingStars } from "@/components/RatingStars";
import { fetchFacility } from "@/lib/api";

const FacilityHero = ({ facility }) => (
  <section data-testid="facility-detail-hero" className="relative overflow-hidden bg-[#09090B] text-white">
    <img data-testid="facility-detail-image" src={facility.image_url} alt={facility.name} className="absolute inset-0 h-full w-full object-cover object-center opacity-45" />
    <div data-testid="facility-detail-overlay" className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/20" />
    <div className="relative mx-auto max-w-[1600px] px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
      <Link data-testid="facility-back-link" to="/" className="inline-flex items-center gap-2 rounded-md bg-white/10 px-3 py-2 text-sm font-bold text-white backdrop-blur-md transition-colors duration-200 hover:bg-white/20">
        <ArrowLeft data-testid="facility-back-icon" size={18} weight="bold" /> Back to map
      </Link>
      <p data-testid="facility-detail-location" className="mt-8 text-xs font-bold uppercase tracking-[0.2em] text-[#FF5500]">{facility.city}, {facility.state}</p>
      <h1 data-testid="facility-detail-name" className="mt-4 max-w-4xl font-display text-4xl font-black leading-none tracking-normal text-white sm:text-5xl lg:text-6xl">{facility.name}</h1>
      <div data-testid="facility-detail-rating" className="mt-5 flex flex-wrap items-center gap-3 text-lg font-black">
        <RatingStars value={facility.rating_average} size={20} testId="facility-detail-stars" />
        {facility.rating_average} from {facility.rating_count} customer ratings
      </div>
    </div>
  </section>
);

const FacilityInfoCard = ({ facility }) => (
  <div data-testid="facility-contact-card" className="border border-zinc-200 bg-white p-5">
    <h2 data-testid="facility-contact-title" className="font-display text-2xl font-black tracking-normal">Facility information</h2>
    <div className="mt-5 grid gap-4 text-sm font-semibold text-[#52525B]">
      <p data-testid="facility-address-detail" className="flex gap-3"><MapPin className="shrink-0 text-[#FF5500]" size={20} weight="fill" /> {facility.address}</p>
      <p data-testid="facility-phone-detail" className="flex gap-3"><Phone className="shrink-0 text-[#FF5500]" size={20} weight="fill" /> {facility.phone}</p>
      <p data-testid="facility-hours-detail" className="flex gap-3"><Clock className="shrink-0 text-[#FF5500]" size={20} weight="fill" /> {facility.hours}</p>
    </div>
  </div>
);

const FacilityServicesCard = ({ facility }) => (
  <div data-testid="facility-services-card" className="border border-zinc-200 bg-white p-5">
    <h2 data-testid="facility-services-title" className="font-display text-2xl font-black tracking-normal">Service capabilities</h2>
    <div data-testid="facility-services-list" className="mt-5 flex flex-wrap gap-2">
      {facility.services.map((service) => (
        <span data-testid={`facility-detail-service-${service.toLowerCase().replaceAll(" ", "-")}`} key={service} className="inline-flex items-center gap-2 rounded-sm border border-zinc-200 px-3 py-2 text-sm font-bold text-[#52525B]">
          <Wrench size={16} weight="bold" /> {service}
        </span>
      ))}
    </div>
    <div className="mt-6 grid grid-cols-2 gap-3">
      <div data-testid="facility-service-bays-stat" className="border border-zinc-200 p-4">
        <p data-testid="facility-service-bays-value" className="font-display text-3xl font-black tracking-normal">{facility.service_bays}</p>
        <p data-testid="facility-service-bays-label" className="text-xs font-bold uppercase tracking-[0.18em] text-[#52525B]">Service bays</p>
      </div>
      <div data-testid="facility-response-stat" className="border border-zinc-200 p-4">
        <p data-testid="facility-response-value" className="font-display text-3xl font-black tracking-normal">{facility.response_time_minutes}m</p>
        <p data-testid="facility-response-label" className="text-xs font-bold uppercase tracking-[0.18em] text-[#52525B]">Target response</p>
      </div>
    </div>
  </div>
);

const FacilityReviewsPanel = ({ ratings }) => (
  <div data-testid="facility-reviews-panel" className="border border-zinc-200 bg-white p-5">
    <p data-testid="facility-reviews-eyebrow" className="text-xs font-bold uppercase tracking-[0.18em] text-[#0055FF]">Published feedback</p>
    <h2 data-testid="facility-reviews-title" className="mt-2 font-display text-3xl font-black tracking-normal">Customer reviews</h2>
    <div data-testid="facility-reviews-list" className="mt-6 grid gap-3">
      {ratings.map((rating) => (
        <article data-testid={`facility-review-${rating.id}`} key={rating.id} className="border border-zinc-200 p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 data-testid={`facility-review-name-${rating.id}`} className="font-display text-xl font-black tracking-normal">{rating.customer_name}</h3>
              <p data-testid={`facility-review-fleet-${rating.id}`} className="text-sm font-bold uppercase tracking-[0.14em] text-[#52525B]">{rating.fleet_type}</p>
            </div>
            <RatingStars value={rating.score} testId={`facility-review-stars-${rating.id}`} />
          </div>
          <p data-testid={`facility-review-comment-${rating.id}`} className="mt-3 text-base leading-7 text-[#52525B]">“{rating.comment}”</p>
        </article>
      ))}
    </div>
  </div>
);

export default function FacilityDetailPage() {
  const { facilityId } = useParams();
  const [facility, setFacility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadFacility = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setFacility(await fetchFacility(facilityId));
    } catch {
      setError("Facility details are unavailable.");
    } finally {
      setLoading(false);
    }
  }, [facilityId, setError, setFacility, setLoading]);

  useEffect(() => {
    loadFacility();
  }, [loadFacility]);

  return (
    <AppShell>
      {loading && <p data-testid="facility-detail-loading" className="p-8 font-bold text-[#52525B]">Loading facility...</p>}
      {error && <p data-testid="facility-detail-error" className="p-8 font-bold text-red-700">{error}</p>}
      {facility && (
        <>
          <FacilityHero facility={facility} />

          <section data-testid="facility-detail-content" className="technical-grid px-4 py-8 sm:px-6 lg:px-8">
            <div className="mx-auto grid max-w-[1600px] gap-6 lg:grid-cols-[0.85fr_1.15fr]">
              <div data-testid="facility-info-panel" className="space-y-4">
                <FacilityInfoCard facility={facility} />
                <FacilityServicesCard facility={facility} />
                <RatingForm facility={facility} onRated={setFacility} />
              </div>
              <FacilityReviewsPanel ratings={facility.ratings} />
            </div>
          </section>
        </>
      )}
    </AppShell>
  );
}